import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Icon } from '../../../globalUtils/globalIcons';
import { useAppDispatch, useAppSelector, HorizontalNavbar, CustomTooltipWithLabel } from '../../../globalUtils/globalExports';
import { getAcReferenceDataReducer, getacNocDashboardReducer } from '../../rmsReduxStore/acReduxExports';
import { fetchAcNocDashboardAlertsList, setFlagAlert, setChargerIdsForRedirection } from '../../rmsReduxStore/acNocDashboardRedux/acNocDashboardCreator';
import { fetchAcAlarmSeverityReferenceData, fetchAcAlarmTypeReferenceData } from '../../rmsReduxStore/acReferenceDataRedux/acReferenceDataCreator';
import { getCurrentAcFiltersValues } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import { formatRelativeTimeForTableIST } from '../../../globalUtils/globalHooks';
import { toast } from 'react-toastify';
import CustomPulseLoader from '../../../components/softwareReleaseManagement/dashboard/CustomPulseLoader';
import AcCustomFilterLayout, { AcCustomFilterProp } from '../dashboard/AcCustomFilterLayout';
import './NocAlertPanel.css';

interface IAlertsList {
  id: string;
  alarmName: string;
  setTime: string;
  severity: string;
  status: string;
  chargerName: string;
  ocppId: string;
  chargingStationName: string;
  customerName: string;
  productType: string;
  flaggedAt?: string;
  connectorNo?: number;
  connectorType?: string;
  chargerId?: string;
}

interface FilterItem {
  key: string;
  values: string[];
}

interface FilterOption {
  id: string;
  value: string;
  label: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  alertsLists?: IAlertsList[] | null;
  ToogleAlertFilter: () => void;
  isFlagged?: boolean;
  alertCount?: number;
}

export default function AcAlertsPanel({ open, onClose, alertCount }: Props) {
  const dispatch = useAppDispatch();
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [localAlerts, setLocalAlerts] = useState<IAlertsList[]>([]);
  const [selectedNav, setSelectedNav] = useState<'latestalerts' | 'flaggedalerts'>('latestalerts');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterItem[]>([]);
  const [selectedFiltersData, setSelectedFiltersData] = useState<{ severity: FilterOption[] | null; alarmType: FilterOption[] | null }>({ severity: null, alarmType: null });
  const { alarmTypeReferenceData, alarmSeverityReferenceData } = useAppSelector(getAcReferenceDataReducer);
  const { nocDashboardAlertsList: reduxAlertsList } = useAppSelector(getacNocDashboardReducer);
  const nocDashboardAlertsList = reduxAlertsList as IAlertsList[] | null;

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const toggleDiagnostics = (id: string) => {
    setOpenDropdownId(prev => (prev === id ? null : id));
  };

  const closeDropdown = () => setOpenDropdownId(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.diagnostics-wrapper')) {
        closeDropdown();
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleChargerNavigation = (chargerId: string, chargerVisibleId: string, selectedNavTab: string) => {
    dispatch(setChargerIdsForRedirection([chargerId]));
    window.open(`/ac/charger?chargerId=${chargerVisibleId}&redirectedFrom=nocDashboard&selectedNav=${selectedNavTab}`, '_blank');
  };

  const markFlagAlert = async (alertId?: string, actionType?: string) => {
    const res = await dispatch(setFlagAlert({ alertId, actionType }));
    if (res?.status === 200) {
      toast.success(res?.data?.message || 'Alert flagged successfully');
      setLocalAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId
            ? { ...alert, flaggedAt: actionType === 'unflagAlert' ? undefined : new Date().toISOString() }
            : alert
        )
      );
    } else {
      toast.error(res?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleNavBtnClick = useCallback((val) => {
    setSelectedNav(val);
  }, []);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    dispatch(fetchAcNocDashboardAlertsList({
      filters: activeFilters,
      searchTerm: '',
      fetchType: selectedNav === 'flaggedalerts' ? 'flaggedAlerts' : ''
    }));
  }, [activeFilters, selectedNav, dispatch]);

  useEffect(() => {
    setIsLoading(false);
    if (nocDashboardAlertsList) {
      const sorted = [...nocDashboardAlertsList].sort((a, b) => {
        const aFlag = a.flaggedAt ? new Date(a.flaggedAt).getTime() : 0;
        const bFlag = b.flaggedAt ? new Date(b.flaggedAt).getTime() : 0;
        if (aFlag && bFlag) return bFlag - aFlag;
        if (aFlag) return -1;
        if (bFlag) return 1;
        return 0;
      });
      setLocalAlerts(sorted);
    }
  }, [nocDashboardAlertsList]);

  useEffect(() => {
    if (open) {
      setSelectedNav('latestalerts');
      const initialFilters = getCurrentAcFiltersValues('nocDashboard', false);
      setActiveFilters(initialFilters);
      setSelectedFiltersData({ severity: null, alarmType: null });
      dispatch(fetchAcAlarmSeverityReferenceData());
      dispatch(fetchAcAlarmTypeReferenceData());
    }
  }, [open]);

  const headerdata = [
    { id: 'latestalerts', text: 'Latest Alerts', isAccessible: true },
    { id: 'flaggedalerts', text: 'Flagged Alerts', isAccessible: true },
  ];

  const navBarconfigurations = useMemo(() => {
    return {
      selectedNav: selectedNav,
      navList: headerdata?.filter((nav) => nav?.isAccessible),
      onNavClick: handleNavBtnClick,
    };
  }, [selectedNav]);

  const handleFilterDataChange = async (
    val: FilterOption | FilterOption[],
    type: 'severity' | 'alarmType'
  ) => {
    setIsLoading(true);
    const filters = getCurrentAcFiltersValues('nocDashboard', false);
    const updatedFilters = [...filters];

    if (type === 'severity') {
      const selectedVal = val as FilterOption;
      const index = updatedFilters.findIndex(f => f.key === 'severity');
      if (index > -1) {
        updatedFilters[index].values = [selectedVal.value];
      } else {
        updatedFilters.push({ key: 'severity', values: [selectedVal.value] });
      }
      setSelectedFiltersData(prev => ({ ...prev, severity: [selectedVal] }));
    } else if (type === 'alarmType') {
      const selectedVals = val as FilterOption[];
      const values = selectedVals.map(v => v.value);
      const index = updatedFilters.findIndex(f => f.key === 'alarmType');
      if (index > -1) {
        updatedFilters[index].values = values;
      } else {
        updatedFilters.push({ key: 'alarmType', values });
      }
      setSelectedFiltersData(prev => ({ ...prev, alarmType: selectedVals }));
    }

    if (!updatedFilters.some(f => f.key === 'severity') && selectedFiltersData.severity?.length) {
      updatedFilters.push({ key: 'severity', values: selectedFiltersData.severity.map(v => v.value) });
    }
    if (!updatedFilters.some(f => f.key === 'alarmType') && selectedFiltersData.alarmType?.length) {
      updatedFilters.push({ key: 'alarmType', values: selectedFiltersData.alarmType.map(v => v.value) });
    }
    setActiveFilters(updatedFilters);
  };

  const FilterProps: AcCustomFilterProp[] = useMemo(() => {
    return [
      {
        filterLabel: 'Severity',
        filterId: 'category',
        filterType: 'dropdown',
        filterDropdownProps: {
          selectValue: selectedFiltersData.severity,
          selectOptions: alarmSeverityReferenceData?.filter((item) => item.id !== 'No Alarm') ?? [],
          handleSelectOptionChange: (val): void => { handleFilterDataChange(val, 'severity'); },
          selectDropDownId: 'alarm-severity-filter-dropdown',
          isMultiSelect: false,
          renderToBody: true,
        },
      },
      {
        filterLabel: 'Alarms',
        filterType: 'dropdown',
        filterId: 'alarmType',
        filterDropdownProps: {
          selectValue: selectedFiltersData.alarmType,
          selectOptions: alarmTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => { handleFilterDataChange(val, 'alarmType'); },
          selectDropDownId: 'alarm-alarmType-filter-dropdown',
          isMultiSelect: true,
          renderToBody: true,
        },
      },
    ];
  }, [alarmTypeReferenceData, alarmSeverityReferenceData, selectedFiltersData]);

  const handleChargerClicked = (chargerSerialId: string, chargerId?: string) => {
    if (chargerId) dispatch(setChargerIdsForRedirection([chargerId]));
    window.open(`/ac/charger?chargerId=${chargerSerialId}&redirectedFrom=nocDashboard`, '_blank');
  };

  if (!open) return null;

  return (
    <div>
      <div className='noc-dashboard-alert-overlay'>
        <div className='noc-dashboard-alert-box'>
          {/* Header */}
          <div className='noc-dashboard-alert-header'>
            <div className='noc-dashboard-alert-title'>
              <HorizontalNavbar
                selectedNav={navBarconfigurations?.selectedNav}
                navList={navBarconfigurations?.navList}
                onNavClick={(event, val) => { event?.stopPropagation(); navBarconfigurations?.onNavClick?.(val); }}
              />
            </div>
            <div className='noc-dashboard-alert-header-actions'>
              <button
                className="noc-dashboard-alert-chip-filter"
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                Filters
              </button>
              {filterAnchorEl && (
                <div className='noc-dashboard-alert-filter'>
                  <AcCustomFilterLayout
                    filters={FilterProps}
                    requiredReferenceDataCallbacks={[]}
                    filterAnchorEl={filterAnchorEl}
                    setFilterAnchorEl={setFilterAnchorEl}
                    excludeGlobalFilters={true}
                  />
                </div>
              )}
              <button className='noc-dashboard-alert-close' onClick={onClose}>✕</button>
            </div>
          </div>

          <div className='noc-dashboard-alert-list'>
            {isLoading && (
              <div className='noc-dashboard-alert-loader'>
                <CustomPulseLoader showLoader={isLoading} size={20} />
              </div>
            )}
            {!isLoading && (!localAlerts || localAlerts.length === 0) && (
              <div className="noc-dashboard-alert-empty">
                <Icon iconClassName='rms__icon no__alarms__icon' />
                No alerts available
              </div>
            )}
            {!isLoading && localAlerts && localAlerts.length > 0 &&
              localAlerts.map((alert, index) => {
                const isTopAlert = index < (alertCount ?? 0);
                return (
                  <div
                    key={index}
                    className={`noc-dashboard-alert-card ${isTopAlert ? 'noc-dashboard-alert-card--highlight' : ''}`}
                  >
                    <div className='noc-dashboard-alert-info-block'>
                      <div className='noc-dashboard-alert-device-row'>
                        <div className='noc-dashboard-icon-row'>
                          <Icon iconClassName='noc-dashboard-right-panel-charger-icon' />
                          <span
                            className='noc-dashboard-alert-device-id'
                            onClick={() => handleChargerClicked(alert?.chargerName, alert?.chargerId)}
                          >
                            {alert?.chargerName}
                          </span>
                          <span className='noc-dashboard-alert-ocpp-id-label'>|</span>
                          <div className='noc-dashboard-alert-ocpp-id-value'>{alert?.ocppId}</div>
                          <span className='noc-dashboard-alert-ocpp-id-label'>|</span>
                          <Icon iconClassName='rms__icon noc-dashboard-right-panel-user-icon' />
                          <CustomTooltipWithLabel label={alert?.customerName ?? ''} labelId={`noc-alert-customer-${alert?.id}`} containerClassName='noc-dashboard-alert-ocpp-id-value noc-dashboard-alert-customer-name' tooltipZIndex={10000} />
                          <span className='noc-dashboard-alert-ocpp-id-label'>|</span>
                          <span className='noc-dashboard-alert-chip-tag'>{alert?.productType}</span>
                        </div>

                        <div className='noc-dashboard-alert-actions'>
                          <div className="diagnostics-wrapper">
                            <button
                              className='noc-dashboard-alert-dropdown'
                              onClick={(e) => { e.stopPropagation(); toggleDiagnostics(alert.id); }}
                            >
                              <span>Diagnostics</span>
                              <span className='noc-dashboard-dropdown-icon'>
                                <Icon iconClassName='rms__icon noc-dashboard-hide-metric-card-bar-icon' />
                              </span>
                            </button>
                            {openDropdownId === alert.id && (
                              <div className='diagnostics-menu'>
                                <div className='diagnostics-item' onClick={() => handleChargerNavigation(alert?.chargerId ?? '', alert?.chargerName, 'settings')}>Set Parameters</div>
                                <div className='diagnostics-item' onClick={() => handleChargerNavigation(alert?.chargerId ?? '', alert?.chargerName, 'remote_commands')}>Remote Command</div>
                              </div>
                            )}
                          </div>

                          {alert?.flaggedAt ? (
                            <div className='noc-dashboard-flag-btn' onClick={() => markFlagAlert(alert?.id, 'unflagAlert')}>
                              <Icon iconClassName='noc-dashboard-alert-panel-flag-icon' />
                            </div>
                          ) : (
                            <div className='noc-dashboard-flag-btn' onClick={() => markFlagAlert(alert?.id)}>
                              <Icon iconClassName='noc-dashboard-alert-panel-unflag-icon' />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='noc-dashboard-alert-desc-row'>
                        <div className='noc-dashboard-alert-desc-sub-row'>
                          <div className={`rms__icon ${alert?.severity.toLowerCase()}__alarm__icon`}></div>
                          <span className='noc-dashboard-alert-desc-row-text'>
                            {alert?.alarmName}
                            {alert?.connectorNo && alert?.connectorType && (
                              <> at {alert.connectorNo}-{alert.connectorType}</>
                            )}
                          </span>
                        </div>
                        <div className='noc-dashboard-alert-timestamp'>
                          Set Time : {formatRelativeTimeForTableIST(alert?.setTime)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      </div>
    </div>
  );
}
